import { useState, useEffect, type ReactNode } from 'react'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import MapRoundedIcon from '@mui/icons-material/MapRounded'
import { Button, Pressable, ControlNotice } from '../Common'
import { AiGeneratorPanel } from '../AiGeneratorPanel'
import { CreditBalance } from '../Credits/CreditBalance'
import { MemberNexusHero } from './controls/MemberNexusPrimitives'
import { trackPulseReportEvent } from '../../services/pulseReportTelemetry'
import type { AiChatSession } from '../../services/aiChatHistoryService'
import type { AiOperatingMode } from '../../types/ai'
import type { AiChatHandoffRequest } from '../../types/aiChatSource'
import type { MemberControls, MemberAiDailyBudgetCounter } from '../../types/memberControls'
import { subscribeToMyEffectiveControls } from '../../services/memberControlsService'
import { useTimeline } from '../../context/useTimeline'
import { useWorkspace } from '../../context/useWorkspace'
import { useNls } from '../../nls/nlsProvider'
import './memberAiChat.css'

export const MemberAiAgentTab = ({
  walletCredits,
  openSessionRequest,
  handoffRequest,
  initialMode = 'CREATE',
  lockedMode,
  quickActions,
  historyCount,
  onOpenHistory,
  onWalletClick,
  onNewChatStarted,
  workspaceId,
  effectiveControls,
  aiBudgetCounter,
}: {
  walletCredits: number
  openSessionRequest?: { session: AiChatSession; nonce: number } | null
  handoffRequest?: AiChatHandoffRequest | null
  initialMode?: AiOperatingMode
  lockedMode?: AiOperatingMode
  quickActions?: ReactNode
  historyCount?: number
  onOpenHistory?: () => void
  onWalletClick?: () => void
  onNewChatStarted?: () => void
  workspaceId?: string
  effectiveControls?: MemberControls | null
  aiBudgetCounter?: MemberAiDailyBudgetCounter | null
}) => {
  const { t } = useNls()
  const { activeWorkspaceId } = useWorkspace()
  const { memberProfile } = useTimeline()
  const effectiveWorkspaceId = workspaceId ?? activeWorkspaceId
  const [subscribedControls, setSubscribedControls] = useState<MemberControls | null>(null)

  useEffect(() => {
    if (effectiveControls !== undefined) return
    if (!effectiveWorkspaceId) return
    return subscribeToMyEffectiveControls(effectiveWorkspaceId, setSubscribedControls)
  }, [effectiveWorkspaceId, effectiveControls])

  const controls = effectiveControls !== undefined ? effectiveControls : subscribedControls
  const aiControl = controls?.ai
  const aiMode = aiControl?.mode ?? 'INHERIT'
  const counter = aiBudgetCounter ?? memberProfile?.aiBudgetCounter
  const committed = counter?.committed ?? 0
  const dailyCredits = aiControl?.dailyCredits ?? 0

  let noticeMessage: string | null = null
  if (aiMode === 'OFF') {
    noticeMessage = t('NLSControlNoticeAiOff')
  } else if (aiMode === 'BUDGET' && dailyCredits > 0 && committed >= dailyCredits) {
    noticeMessage = t('NLSControlNoticeAiBudgetExceeded')
  }

  const [mode, setMode] = useState<AiOperatingMode>(initialMode)
  const [prevInitialMode, setPrevInitialMode] = useState(initialMode)
  if (!lockedMode && prevInitialMode !== initialMode) {
    setPrevInitialMode(initialMode)
    setMode(initialMode)
  }
  const effectiveMode = lockedMode ?? mode
  const toggleMode = () => {
    if (lockedMode) return
    const next = mode === 'CREATE' ? 'EXECUTE' : 'CREATE'
    trackPulseReportEvent({ name: 'ai_agent_mode_toggled', props: { from: mode, to: next } })
    setMode(next)
  }
  const heroTitle = effectiveMode === 'CREATE' ? 'Create' : 'Execute'
  return (
    <div className="memberAiAgentTab" data-ui-name={effectiveMode === 'CREATE' ? 'UI-MPAI-CRE' : 'UI-MPAI-EXE'}>
      <MemberNexusHero
        eyebrow="AI Magic"
        title={heroTitle}
        rightSlot={(
          <div className="memberAiHeroActions">
            {!lockedMode ? (
              <Button variant="secondary" size="md" onClick={toggleMode}>
                {effectiveMode === 'CREATE' ? <AutoAwesomeRoundedIcon fontSize="small" /> : <MapRoundedIcon fontSize="small" />}
                <span>{heroTitle}</span>
              </Button>
            ) : null}
            {/* P2: ⋯ hidden on Create/Execute. In-chat "New chat" pill (§4.8) covers the
             * primary session action. Wiring rename, delete, and clear requires lifting
             * AiGeneratorPanel session state, so it remains deferred. */}
            {typeof historyCount === 'number' && onOpenHistory ? (
              <Pressable className="memberNexusHero__chip memberTapRipple" onClick={onOpenHistory}>
                <span>History</span>
                <strong>{historyCount}</strong>
              </Pressable>
            ) : null}
            <CreditBalance value={walletCredits} variant="bare" scope="workspace" label={null} className="memberAiWalletChip" onClick={onWalletClick} title="Workspace credits" />
          </div>
        )}
      />
      {noticeMessage ? (
        <ControlNotice
          tone="warning"
          message={noticeMessage}
          testId="member-ai-tab-notice"
          className="memberAiAgentControlNotice"
        />
      ) : null}
      {quickActions ? <div className="memberAiAgentQuickActions">{quickActions}</div> : null}
      <div className="memberAiAgentPanel">
        <div className="memberAiMagicHeroGlyph memberAiAgentGlyph" aria-hidden="true">
          {effectiveMode === 'CREATE' ? <AutoAwesomeRoundedIcon fontSize="inherit" /> : <MapRoundedIcon fontSize="inherit" />}
        </div>
        <AiGeneratorPanel
          isOpen
          activeBoard="DASHBOARD"
          externalOpenSessionRequest={openSessionRequest ?? null}
          forcedOperatingMode={effectiveMode}
          hideModeTabs
          layout="mobile"
          handoffRequest={handoffRequest ?? null}
          onNewChatStarted={onNewChatStarted}
          effectiveControls={controls}
          aiBudgetCounter={counter}
        />
      </div>
    </div>
  )
}

